/**
 * Decimal hours on the web side: strings in, strings out, integer
 * hundredths between.
 *
 * sumHours is the mirror of sumHours in api/_shared/repos/staffHours.ts,
 * so a total shown on a page is the arithmetic the server's summary used
 * and never a float; it stays exact. formatHoursTenths is the one-decimal
 * display figure for My time and the internal-time card: the exact
 * numeric(6,2) value rounded once, half up, to a tenth. Both throw on a
 * value that is not a numeric(6,2) string rather than miscount, because
 * every hours value reaches the page as numeric(6,2) text and anything
 * else is a server bug that should be loud.
 */

const HOURS_RE = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;

function toHundredths(v: string): number {
  const m = HOURS_RE.exec(v.trim());
  if (!m) throw new Error(`hours '${v}' is not a numeric(6,2) string`);
  const whole = Number(m[2]);
  const frac = Number((m[3] ?? '0').padEnd(2, '0'));
  const n = whole * 100 + frac;
  return m[1] === '-' ? -n : n;
}

function fromHundredths(n: number): string {
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  return `${sign}${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`;
}

/** The sum of numeric(6,2) strings as a numeric string; '0.00' for none. */
export function sumHours(values: readonly string[]): string {
  return fromHundredths(values.reduce((acc, v) => acc + toHundredths(v), 0));
}

/**
 * Hours as My time shows them: one decimal at most, rounded half-up once
 * from the exact numeric(6,2) value ('21.30' reads 21.3, '2.00' reads 2,
 * '0.25' reads 0.3). Integer arithmetic on hundredths, never toFixed,
 * which rounds some exact halves down. A negative value is a server bug
 * and throws.
 */
export function formatHoursTenths(value: string): string {
  const h = toHundredths(value);
  if (h < 0) throw new Error(`hours '${value}' is negative`);
  const tenths = Math.floor((h + 5) / 10);
  const whole = Math.floor(tenths / 10);
  const d = tenths % 10;
  return d === 0 ? String(whole) : `${whole}.${d}`;
}
